import { useEffect, useState } from "react";
import { getMarketData } from "../../../services/turgoil-api";
import moment from "moment";
import { get_DD_MM_YYYY, getTime } from "../../../utils/date";
import EurUsdLineChart from "./EurUsdLineChart";
import BrentCrudeLineChart from "./BrentCrudeLineChart";
import LondonGasOilLineChart from "./LondonGasOilLineChart";

moment.locale("et");

export const sortByDateAsc = (items: any[], dateKey: keyof any = "date"): any[] => {
    return [...items].sort(
        (a, b) =>
            moment(a[dateKey] as string, "YYYY-MM-DD").valueOf() -
            moment(b[dateKey] as string, "YYYY-MM-DD").valueOf()
    );
};

export const getPriceMovementBackground = (value: number): string => {
    return value < 0 ? "#c0392b" : "#4f9f5e";
};

export const getMarketInstrumentsBackground = (value: number): string => {
    return value < 0 ? "#c0392b" : "#4f9f5e";
};

export const formatValue = (value: number): string => {
    return value > 0 ? `+${value}` : `${value}`;
};

const MarketData = () => {

    const [mounted, setMounted] = useState(false);
    const [marketData, setMarketData] = useState<any>();
    const [eurUsdFilter, setEurUsdFilter] = useState<string>("month");
    const [londonGasOilFilter, setLondonGasOilFilter] = useState<string>("month");
    const [brentCrudeFilter, setBrentCrudeFilter] = useState<string>("month");

    useEffect(() => {
        setMounted(true);
        getMarketData(eurUsdFilter)
            .then((res) => {
                setMarketData(res);
            })
            .catch((err) => {
                console.error(err);
            });
    }, []);

    useEffect(() => {
        if (!mounted) return; // skip the first run
        getMarketData(eurUsdFilter)
            .then((res) => {
                setMarketData(prev => ({
                    ...prev,
                    eurusd: res.eurusd
                }));
            })
            .catch((err) => {
                console.error(err);
            });
    }, [eurUsdFilter]);

    useEffect(() => {
        if (!mounted) return; // skip the first run
        getMarketData(londonGasOilFilter)
            .then((res) => {
                setMarketData(prev => ({
                    ...prev,
                    gasoil: res.gasoil
                }));
            })
            .catch((err) => {
                console.error(err);
            });
    }, [londonGasOilFilter]);

    useEffect(() => {
        if (!mounted) return; // skip the first run
        getMarketData(brentCrudeFilter)
            .then((res) => {
                setMarketData(prev => ({
                    ...prev,
                    oilbrent: res.oilbrent
                }));
            })
            .catch((err) => {
                console.error(err);
            });
    }, [brentCrudeFilter]);

    const updateDate = get_DD_MM_YYYY(marketData?.updateDatetime);
    const updatedTime = getTime(marketData?.updateDatetime);
    const pricePredictions = [...(marketData?.pricePredictions || [])].sort(
        (a, b) =>
            moment(a.date, "YYYY-MM-DD").valueOf() -
            moment(b.date, "YYYY-MM-DD").valueOf()
    );
    const eurusd = marketData?.eurusd?.map(item => ({
        ...item,
        date: moment(item.date).valueOf() // convert to timestamp
    }));

    const gasoil = marketData?.gasoil?.map(item => ({
        ...item,
        date: moment(item.date).valueOf() // convert to timestamp
    }));

    const oilbrent = marketData?.oilbrent?.map(item => ({
        ...item,
        date: moment(item.date).valueOf() // convert to timestamp
    }));

    return (
        <>
            {marketData?.updateDatetime && (
                <div className="market-info__content-title-container">
                    <div className="market-info__content-title">Hinnaliikumine</div>
                    <div>Uuendatud {updateDate} kell {updatedTime}</div>
                </div>
            )}
            <div className="market-info__content__market-data-row">
                {pricePredictions[0]?.value && (
                    <div className="market-info__content__market-data-box" style={{ paddingBottom: "30px" }}>
                        <div className="market-info__content__market-data-box-name">Homme</div>
                        <div className="market-info__content__market-data-box-value" style={{ color: "#F7F7F7", background: getPriceMovementBackground(pricePredictions[0]?.value) }}>{formatValue(pricePredictions[0]?.value)} €/l</div>
                    </div>
                )}
                {pricePredictions[1]?.value && (
                    <div className="market-info__content__market-data-box" style={{ paddingBottom: "30px" }}>
                        <div className="market-info__content__market-data-box-name">Ülehomme</div>
                        <div className="market-info__content__market-data-box-value" style={{ color: "#F7F7F7", background: getPriceMovementBackground(pricePredictions[1]?.value) }}>{formatValue(pricePredictions[1]?.value)} €/l</div>
                    </div>
                )}
                {pricePredictions[2]?.value && (
                    <div className="market-info__content__market-data-box" style={{ paddingBottom: "30px" }}>
                        <div className="market-info__content__market-data-box-name">Üle-ülehomme</div>
                        <div className="market-info__content__market-data-box-value" style={{ color: "#F7F7F7", background: getPriceMovementBackground(pricePredictions[2]?.value) }}>{formatValue(pricePredictions[2]?.value)} €/l</div>
                    </div>
                )}
            </div>
            <div className="market-info__content-legend">
                <div>
                    <span style={{ fontWeight: 700 }}>Punane</span> - prognoositakse hinnalangust; ostmine sel päeval võib anda soodsama hinna.
                </div>
                <div>
                    <span style={{ fontWeight: 700 }}>Roheline</span> - prognoositakse hinnatõusu; kaalu ostu ajastamist võimalusel teisele päevale või
                    oota hinna stabiliseerumist.
                </div>
            </div>
            <div className="market-info__content-title-container">
                <div className="market-info__content-title">Turuinstrumendid</div>
            </div>
            <div className="market-info__content__market-data-row">
                <div className="market-info__content__market-data-box" >
                    {(marketData?.eurusdRT?.valueDifference !== undefined) && (
                        <>
                            <div className="market-info__content__market-data-box-name" style={{ color: 'black' }}>EUR/USD</div>
                            <div className="market-info__content__market-data-box-value" style={{ color: '#f7f7f7', background: getMarketInstrumentsBackground(marketData?.eurusdRT?.valueDifference) }}>{marketData?.eurusdRT?.value}</div>
                            <div className="market-info__content__market-data-box-value">{marketData?.eurusdRT?.valueDifference}</div>
                        </>
                    )}
                </div>
                <div className="market-info__content__market-data-box" >
                    {(marketData?.gasoilRT?.valueDifference !== undefined) && (
                        <>
                            <div className="market-info__content__market-data-box-name" style={{ color: 'black' }}>London Gas Oil</div>
                            <div className="market-info__content__market-data-box-value" style={{ color: '#f7f7f7', background: getMarketInstrumentsBackground(marketData?.gasoilRT?.valueDifference) }}>{marketData?.gasoilRT?.value}</div>
                            <div className="market-info__content__market-data-box-value">{marketData?.gasoilRT?.valueDifference}</div>
                        </>
                    )}
                </div>
                <div className="market-info__content__market-data-box" >
                    {(marketData?.oilbrentRT?.valueDifference !== undefined) && (
                        <>
                            <div className="market-info__content__market-data-box-name" style={{ color: 'black' }}>Brent Crude</div>
                            <div className="market-info__content__market-data-box-value" style={{ color: '#f7f7f7', background: getMarketInstrumentsBackground(marketData?.oilbrentRT?.valueDifference) }}>{marketData?.oilbrentRT?.value}</div>
                            <div className="market-info__content__market-data-box-value">{marketData?.oilbrentRT?.valueDifference}</div>
                        </>
                    )}
                </div>
            </div>
            <div className="market-info__content-title-container">
                <div className="market-info__content-title">EUR/USD</div>
            </div>
            <div className="market-info__content__market-data-filters-container">
                <div className={`market-info__content__market-data-filter ${eurUsdFilter === "month" ? "selected" : ""}`} onClick={() => setEurUsdFilter("month")}>Kuu</div>
                <div className={`market-info__content__market-data-filter ${eurUsdFilter === "year" ? "selected" : ""}`} onClick={() => setEurUsdFilter("year")}>Aasta</div>
                <div className={`market-info__content__market-data-filter ${eurUsdFilter === "3year" ? "selected" : ""}`} onClick={() => setEurUsdFilter("3year")}>3 aastat</div>
                <div className={`market-info__content__market-data-filter ${eurUsdFilter === "5year" ? "selected" : ""}`} onClick={() => setEurUsdFilter("5year")}>5 aastat</div>
            </div>
            <div className="market-info__content__market-data-table">
                <div style={{ height: '500px', maxWidth: '680px', padding: '15px 0px' }}>
                    <EurUsdLineChart data={eurusd} filter={eurUsdFilter} />
                </div>
            </div>
            <div className="market-info__content-title-container">
                <div className="market-info__content-title">London Gas Oil</div>
            </div>
            <div className="market-info__content__market-data-filters-container" style={{ overflowX: 'auto' }}>
                <div className={`market-info__content__market-data-filter ${londonGasOilFilter === "month" ? "selected" : ""}`} onClick={() => setLondonGasOilFilter("month")}>Kuu</div>
                <div className={`market-info__content__market-data-filter ${londonGasOilFilter === "year" ? "selected" : ""}`} onClick={() => setLondonGasOilFilter("year")}>Aasta</div>
                <div className={`market-info__content__market-data-filter ${londonGasOilFilter === "3year" ? "selected" : ""}`} onClick={() => setLondonGasOilFilter("3year")}>3 aastat</div>
                <div className={`market-info__content__market-data-filter ${londonGasOilFilter === "5year" ? "selected" : ""}`} onClick={() => setLondonGasOilFilter("5year")}>5 aastat</div>
            </div>
            <div className="market-info__content__market-data-table">
                <div style={{ height: '500px', maxWidth: '680px', padding: '15px 0px' }}>
                    <LondonGasOilLineChart data={gasoil} filter={londonGasOilFilter} />
                </div>
            </div>
            <div className="market-info__content-title-container">
                <div className="market-info__content-title">Brent Crude</div>
            </div>
            <div className="market-info__content__market-data-filters-container">
                <div className={`market-info__content__market-data-filter ${brentCrudeFilter === "month" ? "selected" : ""}`} onClick={() => setBrentCrudeFilter("month")}>Kuu</div>
                <div className={`market-info__content__market-data-filter ${brentCrudeFilter === "year" ? "selected" : ""}`} onClick={() => setBrentCrudeFilter("year")}>Aasta</div>
                <div className={`market-info__content__market-data-filter ${brentCrudeFilter === "3year" ? "selected" : ""}`} onClick={() => setBrentCrudeFilter("3year")}>3 aastat</div>
                <div className={`market-info__content__market-data-filter ${brentCrudeFilter === "5year" ? "selected" : ""}`} onClick={() => setBrentCrudeFilter("5year")}>5 aastat</div>
            </div>
            <div className="market-info__content__market-data-table">
                <div style={{ height: '500px', maxWidth: '680px', padding: '15px 0px' }}>
                    <BrentCrudeLineChart data={oilbrent} filter={brentCrudeFilter} />
                </div>
            </div>
        </>
    );
}

export default MarketData;
